/**
 * DescriptionCard — the identity-carrying component. Same shape here as
 * on the marketing + docs sites (see website/src/components/DescriptionCard.astro).
 *
 * Endpoint byline attribution with em-dash prefix, three severity states
 * (attention / note / decrypting), poppy accent stripe when attention is
 * warranted. Everything else defers to the prose.
 */

import type { ComponentChildren, JSX } from 'preact';
import { cn } from '../lib/cn';

export type Severity = 'attention' | 'note' | 'decrypting';

interface Props {
  endpoint: string;
  time: string;
  severity?: Severity | undefined;
  size?: 'default' | 'hero' | undefined;
  acknowledgedBy?: string | undefined;
  acknowledgedAt?: string | undefined;
  actions?: ComponentChildren | undefined;
  children?: ComponentChildren | undefined;
  class?: string | undefined;
  className?: string | undefined;
}

const sevLabel: Record<Severity, string> = {
  attention: 'Attention',
  note: 'Note',
  decrypting: 'Decrypting',
};

export function DescriptionCard(props: Props): JSX.Element {
  const {
    endpoint,
    time,
    severity = 'note',
    size = 'default',
    acknowledgedBy,
    acknowledgedAt,
    actions,
    children,
    class: cls,
    className,
  } = props;

  const isAttention = severity === 'attention';
  const isDecrypting = severity === 'decrypting';
  const isAcked = Boolean(acknowledgedBy);

  return (
    <article
      class={cn(
        'relative rounded-[4px] border border-line bg-surface-2 grid gap-4',
        size === 'hero' ? 'px-10 pt-8 pb-7' : 'px-6 pt-5 pb-5',
        isAttention && 'bg-signal-bg',
        isAcked && 'opacity-70',
        cls,
        className,
      )}
      aria-label={isDecrypting ? 'Decrypting alert' : 'Alert'}
    >
      {isAttention && (
        <span
          aria-hidden
          class="absolute left-0 top-0 bottom-0 w-[3px] bg-signal rounded-l-[4px]"
        />
      )}

      <div class="flex flex-wrap items-baseline gap-x-3.5 gap-y-1 font-mono text-[12px] tabular-nums text-surface-body tracking-[0.01em]">
        <span class="text-surface-fg font-medium before:content-['—_'] before:text-surface-muted">
          {endpoint}
        </span>
        <span class="text-line-strong select-none" aria-hidden>
          ·
        </span>
        <span>{time}</span>
        <span class="text-line-strong select-none" aria-hidden>
          ·
        </span>
        <span
          class={cn(
            'uppercase tracking-[0.08em] text-[11px] font-medium',
            isAttention ? 'text-signal' : 'text-surface-muted',
          )}
        >
          {sevLabel[severity]}
        </span>
        {isAcked && (
          <>
            <span class="text-line-strong select-none" aria-hidden>
              ·
            </span>
            <span class="text-[11px] text-surface-muted tracking-[0.04em]">
              acknowledged by {acknowledgedBy} · {acknowledgedAt}
            </span>
          </>
        )}
      </div>

      {isDecrypting ? (
        <div class="grid gap-2" aria-hidden>
          <span
            class="block h-[14px] rounded-[2px] bg-gradient-to-r from-line via-line-strong to-line animate-pulse"
            style={{ width: '92%' }}
          />
          <span
            class="block h-[14px] rounded-[2px] bg-gradient-to-r from-line via-line-strong to-line animate-pulse"
            style={{ width: '100%' }}
          />
          <span
            class="block h-[14px] rounded-[2px] bg-gradient-to-r from-line via-line-strong to-line animate-pulse"
            style={{ width: '74%' }}
          />
        </div>
      ) : (
        <p
          class={cn(
            'text-surface-fg m-0',
            size === 'hero'
              ? 'text-[20px] leading-[1.5] max-w-[58ch]'
              : 'text-[17px] leading-[1.55] max-w-[62ch]',
          )}
          style={{ textWrap: 'pretty' }}
        >
          {children}
        </p>
      )}

      {actions}
    </article>
  );
}
